import { format } from "date-fns"
import { AlertCircleIcon, CalendarIcon, CircleCheck } from "lucide-react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import InfoIcon from "@/assets/icons/settings/info-icon.tsx"
import { Button } from "@/components/shadcn/button.tsx"
import OverflowMenuItem from "@/components/task-card/overflow-menu-item.tsx"
import { useGetAnimalByIdQuery } from "@/features/farm/api/animals-api.ts"
import AnimalCard from "@/features/farm/components/animal-card/animal-card.tsx"
import { ANIMAL_PROFILE_VIEW } from "@/features/farm/routes/route-list.ts"
import CalendarActivityTimeline from "@/features/gestational-calendar/components/calendar-activity-timeline/calendar-activity-timeline.tsx"
import GestationCalendarActionButton from "@/features/gestational-calendar/components/gestation-calendar-action-button/gestation-calendar-action-button.tsx"
import { useNavigateBirthEvent } from "@/features/gestational-calendar/hooks/use-navigate-birth-event.ts"
import { type GestationCalendar } from "@/features/gestational-calendar/types/calendar-types.ts"
import {
  checkIfTodayOrIfInInterval,
  formatGestationalCalendarInterval,
} from "@/features/gestational-calendar/utils/gestational-calendar-utils.ts"

interface GestationCalendarDetailsProps {
  gestationalCalendar: GestationCalendar
  showActionButtons?: boolean
}

const GestationCalendarDetails: React.FC<GestationCalendarDetailsProps> = ({
  gestationalCalendar,
  showActionButtons = true,
}) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { data: animal } = useGetAnimalByIdQuery(gestationalCalendar.animal)
  const navigateToBirthEvent = useNavigateBirthEvent()

  const navigateToAnimal = () => {
    navigate(ANIMAL_PROFILE_VIEW + `/${animal?.id}`)
  }
  const calvingCalendarActivity = gestationalCalendar.entries.find(
    (entry) => entry.event.metadata?.is_birth_event,
  )

  return (
    <div className={"flex flex-col gap-[10px]"}>
      <OverflowMenuItem onClick={navigateToAnimal} marginInline={false}>
        {animal && <AnimalCard animal={animal} />}
      </OverflowMenuItem>
      {!gestationalCalendar.abortion_confirmed ? (
        <div className={"widget-border flex flex-col gap-3 bg-white p-4"}>
          {!gestationalCalendar.confirmed_calving_date && (
            <div className={"flex items-center gap-3 text-neutral-800"}>
              <InfoIcon className={"scale-75"} />
              {gestationalCalendar.gestation_confirmed ? (
                <p className={"text-base font-medium"}>
                  {t("gestationConfirmed")}
                </p>
              ) : (
                <p className={"text-base font-medium"}>
                  {t("gestationNotConfirmed")}
                </p>
              )}
            </div>
          )}
          <div
            className={
              "flex flex-col items-start justify-between gap-2 font-medium"
            }
          >
            <p>{t("inseminationDate")}:</p>
            <div
              className={
                "widget-border flex w-full items-center justify-center gap-3 p-2 text-neutral-700"
              }
            >
              <CalendarIcon />
              <p className={""}>
                {format(
                  new Date(gestationalCalendar.insemination_date),
                  "dd.MM.yyyy",
                )}
              </p>
            </div>
          </div>
          <div
            className={
              "flex flex-col items-start justify-between gap-2 font-medium"
            }
          >
            <p>
              {gestationalCalendar.confirmed_calving_date
                ? t("calvingDate")
                : t("possibleCalvingInterval")}
              :
            </p>
            <div
              className={
                "widget-border flex w-full items-center justify-center gap-3 p-2 text-neutral-700"
              }
            >
              <CalendarIcon />
              <p>
                {calvingCalendarActivity &&
                !gestationalCalendar.confirmed_calving_date
                  ? formatGestationalCalendarInterval(calvingCalendarActivity)
                  : gestationalCalendar.confirmed_calving_date &&
                    format(
                      gestationalCalendar.confirmed_calving_date,
                      "dd.MM.yyyy",
                    )}
              </p>
            </div>
          </div>
          {gestationalCalendar.confirmed_calving_date && (
            <GestationCalendarActionButton
              text={t("calvingConfirmed")}
              isDone={true}
            />
          )}
          {!gestationalCalendar.confirmed_calving_date &&
            gestationalCalendar.gestation_confirmed &&
            calvingCalendarActivity &&
            checkIfTodayOrIfInInterval(calvingCalendarActivity) && (
              <Button
                size={"lg"}
                className={"w-full gap-2 font-medium text-white"}
                onClick={() => navigateToBirthEvent(gestationalCalendar)}
              >
                <CircleCheck className={"text-white"} />
                {t("confirmCalving")}
              </Button>
            )}
        </div>
      ) : (
        <div
          className={
            "widget-border flex items-center gap-3 bg-white p-4 text-neutral-800"
          }
        >
          <AlertCircleIcon className={"h-10 w-10 text-red-500"} />
          <p className={"text-base font-medium"}>{t("gestationFailed")}</p>
        </div>
      )}
      <CalendarActivityTimeline
        showActionButtons={showActionButtons}
        gestationalCalendar={gestationalCalendar}
      />
    </div>
  )
}

export default GestationCalendarDetails
